import { Box, IconButton, Typography, alpha } from "@mui/material";
import { Close, AutoAwesome } from "@mui/icons-material";

type Props = { summary: string; onClose: () => void; sx?: object };

export const StreamSummaryBanner = ({ summary, onClose, sx }: Props) => (
  <Box
    sx={{
      width: "100%",
      pointerEvents: "auto",
      position: "fixed",
      top: { xs: 56, sm: 64 },
      left: 0,
      right: 0,
      zIndex: 1301,
      display: "flex",
      justifyContent: "center",
      px: { xs: 1, sm: 2 },
      ...sx,
    }}
  >
    <Box
      sx={{
        width: "100%",
        maxWidth: 720,
        
        // Solid gradient background
        background: "linear-gradient(135deg, #1a1b3a 0%, #16172d 100%)",
        
        // Clean border with accent
        border: "1px solid",
        borderColor: alpha("#6535E9", 0.3),
        borderRadius: 2,
        
        // Professional shadow
        boxShadow: `
          0 8px 32px rgba(0, 0, 0, 0.4),
          0 2px 8px rgba(0, 0, 0, 0.3),
          inset 0 1px 0 rgba(255, 255, 255, 0.06)
        `,
        
        // Layout
        display: "flex",
        alignItems: "flex-start", // Changed from center to flex-start
        gap: 1.5,
        p: { xs: 1.5, sm: 2 },
        position: "relative",
        overflow: "hidden",
        
        // Animated accent line
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          height: "2px",
          width: "100%",
          background: "linear-gradient(90deg, #6535E9 0%, #4E33E9 50%, #6535E9 100%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 3s linear infinite",
        },
        
        "@keyframes shimmer": {
          "0%": { backgroundPosition: "200% 0" },
          "100%": { backgroundPosition: "-200% 0" },
        },
      }}
    >
      {/* Icon with glow effect */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 0.5, // Slight top margin to align with text
          flexShrink: 0, // Prevent icon from shrinking
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(101, 53, 233, 0.3) 0%, transparent 70%)",
            animation: "pulse 2s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { transform: "scale(1)", opacity: 0.5 },
              "50%": { transform: "scale(1.2)", opacity: 0.8 },
            },
          }}
        />
        <AutoAwesome
          sx={{
            fontSize: { xs: 20, sm: 22 },
            color: "#6535E9",
            filter: "drop-shadow(0 0 8px rgba(101, 53, 233, 0.5))",
            zIndex: 1,
          }}
        />
      </Box>

      {/* Content container */}
      <Box sx={{ flex: 1, pr: 3 }}> {/* Added padding-right for close button space */}
        {/* Label */}
        <Typography
          sx={{
            fontSize: { xs: "0.625rem", sm: "0.6875rem" },
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#6535E9",
            mb: 0.25,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          Stream Update
          <Box
            component="span"
            sx={{
              px: 0.75,
              py: 0.25,
              bgcolor: alpha("#6535E9", 0.2),
              borderRadius: 10,
              fontSize: "0.625rem",
              fontWeight: 600,
            }}
          >
            LIVE
          </Box>
        </Typography>
        
        {/* Summary text - REMOVED truncation */}
        <Typography
          sx={{
            color: "rgba(255, 255, 255, 0.95)",
            fontSize: { xs: "0.8125rem", sm: "0.875rem" }, // Slightly smaller on mobile
            fontWeight: 400,
            lineHeight: 1.4,
            letterSpacing: "-0.01em",
            whiteSpace: "pre-wrap", // Preserve line breaks if any
            wordBreak: "break-word", // Break long words if needed
          }}
        >
          {summary}
        </Typography>
      </Box>

      {/* Close button */}
      <IconButton
        size="small"
        onClick={onClose}
        sx={{
          position: "absolute",
          top: { xs: 6, sm: 8 },
          right: { xs: 6, sm: 8 },
          p: { xs: 0.5, sm: 0.75 },
          color: alpha("#fff", 0.5),
          bgcolor: alpha("#000", 0.2),
          border: "1px solid",
          borderColor: alpha("#fff", 0.1),
          transition: "all 0.2s ease",
          
          "&:hover": {
            color: alpha("#fff", 0.8),
            bgcolor: alpha("#000", 0.4),
            borderColor: alpha("#6535E9", 0.3),
            transform: "rotate(90deg)",
          },
        }}
      >
        <Close sx={{ fontSize: { xs: 14, sm: 16 } }} />
      </IconButton>
    </Box>
  </Box>
);